import { createAction, createReducer } from "@reduxjs/toolkit"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { api } from "./helpers/api"

// ACTIONS
const requested = createAction("emailDomain/requested")
const received = createAction("emailDomain/received")
const failed = createAction("emailDomain/failed")
const domainRegistered = createAction("emailDomain/domainRegistered")
const updateRequested = createAction("emailDomain/updateRequested")
const domainUpdated = createAction("emailDomain/domainUpdated")
const updateFailed = createAction("emailDomain/updateFailed")
const refreshRequested = createAction("emailDomain/refreshRequested")
const statusRefreshed = createAction("emailDomain/statusRefreshed")
const refreshFailed = createAction("emailDomain/refreshFailed")
const removeRequested = createAction("emailDomain/removeRequested")
const domainRemoved = createAction("emailDomain/domainRemoved")
const removeFailed = createAction("emailDomain/removeFailed")

// Maps backend status strings to frontend enum values
const normalizeStatus = (status) => {
  if (!status) return null
  const map = {
    success: "verified",
    verified: "verified",
    pending: "pending",
    failed: "failed"
  }
  return map[status.toLowerCase()] ?? null
}

// INITIAL STATE
const initialState = {
  ...LOADING_INITIAL_STATE,
  domain: null,
  from_address: null,
  from_name: null,
  verification_status: null, // "pending" | "verified" | "failed" | null
  dkim_status: null,
  ses_domain_status: null,
  ses_verified_at: null,
  dkim_records: [],
  spf_record: null,
  updateLoading: false,
  refreshLoading: false,
  removeLoading: false,
  error: null
}

// REDUCER
const emailDomainReducer = createReducer(initialState, {
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, action) => {
    setReceived(state)
    const { domain, from_email, from_name, verification_status, dkim_status, dkim_records, spf_record } = action.payload
    state.domain = domain
    state.from_address = from_email ?? null
    state.from_name = from_name ?? null
    state.verification_status = normalizeStatus(verification_status)
    state.dkim_status = normalizeStatus(dkim_status)
    state.dkim_records = dkim_records ?? []
    state.spf_record = spf_record ?? null
  },
  [failed.type]: (state, action) => {
    setFailed(state)
    state.error = action.payload
  },
  [domainRegistered.type]: (state, action) => {
    setReceived(state)
    const { domain, dkim_records, spf_record, from_email, from_name } = action.payload
    state.domain = domain
    state.dkim_records = dkim_records
    state.spf_record = spf_record
    state.from_address = from_email ?? null
    state.from_name = from_name ?? null
    state.verification_status = "pending"
  },
  [updateRequested.type]: (state) => {
    state.updateLoading = true
  },
  [domainUpdated.type]: (state, action) => {
    state.updateLoading = false
    state.from_address = action.payload.from_email ?? null
    state.from_name = action.payload.from_name ?? null
  },
  [updateFailed.type]: (state) => {
    state.updateLoading = false
  },
  [refreshRequested.type]: (state) => {
    state.refreshLoading = true
  },
  [statusRefreshed.type]: (state, action) => {
    state.refreshLoading = false
    const { verification_status, dkim_status, ses_domain_status, ses_verified_at } = action.payload
    state.verification_status = normalizeStatus(verification_status)
    state.dkim_status = normalizeStatus(dkim_status)
    state.ses_domain_status = ses_domain_status ?? null
    state.ses_verified_at = ses_verified_at ?? null
  },
  [refreshFailed.type]: (state) => {
    state.refreshLoading = false
  },
  [removeRequested.type]: (state) => {
    state.removeLoading = true
  },
  [domainRemoved.type]: (state) => {
    state.removeLoading = false
    state.domain = null
    state.from_address = null
    state.from_name = null
    state.verification_status = null
    state.dkim_status = null
    state.ses_domain_status = null
    state.ses_verified_at = null
    state.dkim_records = []
    state.spf_record = null
  },
  [removeFailed.type]: (state) => {
    state.removeLoading = false
  }
})

export default emailDomainReducer

// PUBLIC ACTIONS

export const fetchEmailDomain = () =>
  api({
    url: "/guide/domain_verification",
    method: "get",
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })

export const registerEmailDomain = (domain, fromAddress, fromName) =>
  api({
    url: "/guide/domain_verification",
    method: "post",
    data: { domain, email: fromAddress, from_name: fromName },
    onStart: requested.type,
    onSuccess: domainRegistered.type,
    onError: failed.type
  })

export const updateFromAddress = (fromAddress, fromName) =>
  api({
    url: "/guide/domain_verification",
    method: "patch",
    data: { email: fromAddress, from_name: fromName },
    onStart: updateRequested.type,
    onSuccess: domainUpdated.type,
    onError: updateFailed.type
  })

export const removeEmailDomain = () =>
  api({
    url: "/guide/domain_verification",
    method: "delete",
    onStart: removeRequested.type,
    onSuccess: domainRemoved.type,
    onError: removeFailed.type
  })

export const refreshDomainStatus = () =>
  api({
    url: "/guide/domain_verification/status",
    method: "get",
    onStart: refreshRequested.type,
    onSuccess: statusRefreshed.type,
    onError: refreshFailed.type
  })
